import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { io, type Socket } from "socket.io-client";
import {
  Brain,
  Check,
  Clock3,
  Loader2,
  MessageCircle,
  Plus,
  Send,
  ShieldCheck,
  Sparkles,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHead } from "@/components/bits";
import { useI18n } from "@/i18n/i18n-context";
import { useToast } from "@/hooks/use-toast";
import { runtime } from "@/config/runtime";
import { publicApi } from "@/services/api-client";
import { tokenStore } from "@/services/token-store";
import {
  ErrorBox,
  SkeletonRows,
  useServer,
  type ApiRecord,
} from "@/features/finance/kit";
import { cn } from "@/lib/utils";

interface AssistantMessage {
  id: string;
  role: "assistant" | "user";
  content: string;
  created_at?: string;
}

interface AssistantConversation {
  id: string;
  title: string;
  last_message_at?: string | null;
}

interface AssistantMemory {
  id: string;
  content: string;
  created_at?: string;
}

interface AssistantJob {
  id: string;
  name: string;
  template:
    "DAILY_PAYMENT_DIGEST" | "WEEKLY_ACCOUNT_HEALTH" | "PENDING_PAYMENT_REVIEW";
  cadence: "DAILY" | "WEEKLY";
  timezone: string;
  local_time: string;
  day_of_week: number | null;
  enabled: boolean;
  next_run_at: string;
  last_run_at?: string | null;
}

interface AssistantJobRun {
  id: string;
  status: "QUEUED" | "RUNNING" | "COMPLETED" | "FAILED" | "SKIPPED";
  report?: string | null;
  created_at: string;
}

interface AssistantStatus extends ApiRecord {
  enabled: boolean;
  configured: boolean;
  ai_api_enabled: boolean;
  consented: boolean;
  conversations: AssistantConversation[];
  memories: AssistantMemory[];
  jobs: AssistantJob[];
  quick_actions: string[];
}

interface AssistantReplyEvent {
  request_id: string;
  conversation: AssistantConversation;
  message: AssistantMessage;
}

type AssistantSocket = Socket;

export default function MerchantAssistantPage() {
  const { t, lang } = useI18n();
  const { toast } = useToast();
  const status = useServer<AssistantStatus>("/assistant");
  const [accepted, setAccepted] = useState(false);
  const [consenting, setConsenting] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [memoryDraft, setMemoryDraft] = useState("");
  const [savingMemory, setSavingMemory] = useState(false);
  const [deletingMemory, setDeletingMemory] = useState<string | null>(null);
  const [showMemories, setShowMemories] = useState(false);
  const [jobTemplate, setJobTemplate] = useState<AssistantJob["template"]>(
    "DAILY_PAYMENT_DIGEST",
  );
  const [jobCadence, setJobCadence] =
    useState<AssistantJob["cadence"]>("DAILY");
  const [jobTime, setJobTime] = useState("08:00");
  const [savingJob, setSavingJob] = useState(false);
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [jobRuns, setJobRuns] = useState<Record<string, AssistantJobRun[]>>({});
  const [socketConnected, setSocketConnected] = useState(false);
  const [socketTyping, setSocketTyping] = useState(false);
  const socketRef = useRef<AssistantSocket | null>(null);
  const pendingReplyRef = useRef<{
    requestId: string;
    resolve: (reply: AssistantReplyEvent) => void;
    reject: (error: Error) => void;
    timeout: number;
  } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const assistant = status.data;
  const memories = useMemo(() => assistant?.memories ?? [], [assistant]);

  useEffect(() => {
    const timer = window.setInterval(status.reload, 20_000);
    return () => window.clearInterval(timer);
  }, [status.reload]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!assistant?.consented || !assistant.enabled) return;

    const socket = io(runtime.merchantAssistantSocketUrl, {
      path: "/socket.io",
      transports: ["websocket", "polling"],
      auth: (callback) =>
        callback({ token: tokenStore.get()?.accessToken ?? "" }),
      reconnection: true,
      reconnectionAttempts: 8,
      reconnectionDelay: 5_000,
      reconnectionDelayMax: 30_000,
      randomizationFactor: 0.5,
      timeout: 20_000,
    }) as AssistantSocket;
    socketRef.current = socket;

    const onConnect = () => setSocketConnected(true);
    const onDisconnect = () => setSocketConnected(false);
    const onTyping = (event: { active?: boolean }) =>
      setSocketTyping(Boolean(event.active));
    const onReply = (reply: AssistantReplyEvent) => {
      const pending = pendingReplyRef.current;
      if (!pending || pending.requestId !== reply.request_id) return;
      window.clearTimeout(pending.timeout);
      pendingReplyRef.current = null;
      pending.resolve(reply);
    };
    const onError = (event: { request_id?: string; message?: string }) => {
      const pending = pendingReplyRef.current;
      if (!pending || pending.requestId !== event.request_id) return;
      window.clearTimeout(pending.timeout);
      pendingReplyRef.current = null;
      pending.reject(
        new Error(event.message || t("merchantAssistant.replyFailed")),
      );
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("assistant:typing", onTyping);
    socket.on("assistant:reply", onReply);
    socket.on("assistant:error", onError);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("assistant:typing", onTyping);
      socket.off("assistant:reply", onReply);
      socket.off("assistant:error", onError);
      socket.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
      const pending = pendingReplyRef.current;
      if (pending) {
        window.clearTimeout(pending.timeout);
        pending.reject(new Error(t("merchantAssistant.connectionLost")));
        pendingReplyRef.current = null;
      }
    };
  }, [assistant?.consented, assistant?.enabled, t]);

  useEffect(() => {
    if (!assistant?.consented || conversationId || messages.length > 0) return;
    const latest = assistant.conversations?.[0];
    if (latest) void openConversation(latest.id);
    // Initial conversation restoration should only run when the status loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistant?.consented, assistant?.conversations]);

  const openConversation = async (id: string) => {
    setLoadingConversation(true);
    try {
      const result = await publicApi<{
        conversation: AssistantConversation;
        messages: AssistantMessage[];
      }>(`/assistant/conversations/${id}`);
      setConversationId(result.data.conversation.id);
      setMessages(result.data.messages ?? []);
    } catch (error) {
      toast({
        title: t("merchantAssistant.loadFailed"),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setLoadingConversation(false);
    }
  };

  const enableAssistant = async (event: FormEvent) => {
    event.preventDefault();
    if (!accepted) return;
    setConsenting(true);
    try {
      await publicApi("/assistant/consent", {
        method: "POST",
        body: { accepted: true },
      });
      status.reload();
    } catch (error) {
      toast({
        title: t("merchantAssistant.enableFailed"),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setConsenting(false);
    }
  };

  const sendMessage = async (event?: FormEvent, quickPrompt?: string) => {
    event?.preventDefault();
    const content = (quickPrompt ?? draft).trim();
    if (!content || busy) return;
    setBusy(true);
    setDraft("");
    const optimistic: AssistantMessage = {
      id: `local-${Date.now()}`,
      role: "user",
      content,
    };
    setMessages((current) => [...current, optimistic]);
    try {
      const socket = socketRef.current;
      if (!socket?.connected)
        throw new Error(t("merchantAssistant.connectionLost"));
      const requestId = crypto.randomUUID();
      const result = await new Promise<AssistantReplyEvent>(
        (resolve, reject) => {
          const timeout = window.setTimeout(() => {
            pendingReplyRef.current = null;
            reject(new Error(t("merchantAssistant.connectionLost")));
          }, 45_000);
          pendingReplyRef.current = { requestId, resolve, reject, timeout };
          socket.emit(
            "assistant:message",
            {
              request_id: requestId,
              conversation_id: conversationId,
              message: content,
              language: lang,
            },
            (acknowledgement: {
              success: boolean;
              error?: { message?: string };
            }) => {
              if (!acknowledgement.success) {
                const pending = pendingReplyRef.current;
                if (!pending || pending.requestId !== requestId) return;
                window.clearTimeout(pending.timeout);
                pendingReplyRef.current = null;
                reject(
                  new Error(
                    acknowledgement.error?.message ||
                      t("merchantAssistant.replyFailed"),
                  ),
                );
              }
            },
          );
        },
      );
      setConversationId(result.conversation.id);
      setMessages((current) => [...current, result.message]);
      status.reload();
    } catch (error) {
      setDraft(content);
      setMessages((current) =>
        current.filter((message) => message.id !== optimistic.id),
      );
      toast({
        title: t("merchantAssistant.replyFailed"),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const saveMemory = async (event: FormEvent) => {
    event.preventDefault();
    if (!memoryDraft.trim() || savingMemory) return;
    setSavingMemory(true);
    try {
      await publicApi("/assistant/memories", {
        method: "POST",
        body: { content: memoryDraft.trim() },
      });
      setMemoryDraft("");
      status.reload();
      toast({ title: t("merchantAssistant.memorySaved") });
    } catch (error) {
      toast({
        title: t("merchantAssistant.memoryFailed"),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setSavingMemory(false);
    }
  };

  const deleteMemory = async (id: string) => {
    setDeletingMemory(id);
    try {
      await publicApi(`/assistant/memories/${id}`, { method: "DELETE" });
      status.reload();
    } catch (error) {
      toast({
        title: t("merchantAssistant.memoryFailed"),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setDeletingMemory(null);
    }
  };

  const deleteConversation = async (id: string) => {
    try {
      await publicApi(`/assistant/conversations/${id}`, { method: "DELETE" });
      if (conversationId === id) {
        setConversationId(null);
        setMessages([]);
      }
      status.reload();
    } catch (error) {
      toast({
        title: t("merchantAssistant.loadFailed"),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    }
  };

  const createJob = async (event: FormEvent) => {
    event.preventDefault();
    setSavingJob(true);
    try {
      await publicApi("/assistant/jobs", {
        method: "POST",
        body: {
          name: t(`merchantAssistant.templates.${jobTemplate}`),
          template: jobTemplate,
          cadence: jobCadence,
          timezone: "Africa/Douala",
          local_time: jobTime,
          language: lang,
          ...(jobCadence === "WEEKLY" ? { day_of_week: 1 } : {}),
        },
      });
      status.reload();
      toast({ title: t("merchantAssistant.jobCreated") });
    } catch (error) {
      toast({
        title: t("merchantAssistant.jobFailed"),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setSavingJob(false);
    }
  };

  const loadJobRuns = async (id: string, pollAttempt = 0) => {
    try {
      const result = await publicApi<{ data: AssistantJobRun[] }>(
        `/assistant/jobs/${id}/runs`,
      );
      const runs = result.data.data ?? [];
      setJobRuns((current) => ({ ...current, [id]: runs }));
      if (
        pollAttempt < 6 &&
        runs.some((run) => run.status === "QUEUED" || run.status === "RUNNING")
      ) {
        window.setTimeout(() => void loadJobRuns(id, pollAttempt + 1), 3_000);
      }
    } catch {
      toast({
        title: t("merchantAssistant.jobFailed"),
        variant: "destructive",
      });
    }
  };

  const runJob = async (job: AssistantJob) => {
    try {
      await publicApi(`/assistant/jobs/${job.id}/run`, { method: "POST" });
      setExpandedJob(job.id);
      status.reload();
      await loadJobRuns(job.id);
      toast({ title: t("merchantAssistant.jobQueued") });
    } catch (error) {
      toast({
        title: t("merchantAssistant.jobFailed"),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    }
  };

  const toggleJob = async (job: AssistantJob) => {
    try {
      await publicApi(`/assistant/jobs/${job.id}`, {
        method: "PATCH",
        body: { enabled: !job.enabled },
      });
      status.reload();
    } catch (error) {
      toast({
        title: t("merchantAssistant.jobFailed"),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    }
  };

  const deleteJob = async (job: AssistantJob) => {
    if (!window.confirm(t("merchantAssistant.confirmDeleteJob"))) return;
    try {
      await publicApi(`/assistant/jobs/${job.id}`, { method: "DELETE" });
      status.reload();
      setJobRuns((current) => {
        const next = { ...current };
        delete next[job.id];
        return next;
      });
    } catch (error) {
      toast({
        title: t("merchantAssistant.jobFailed"),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    }
  };

  if (status.loading) {
    return (
      <div className="mx-auto max-w-5xl space-y-5 p-5 sm:p-8">
        <SkeletonRows n={4} />
      </div>
    );
  }

  if (status.error) {
    return (
      <div className="mx-auto max-w-5xl p-5 sm:p-8">
        <ErrorBox error={status.error} onRetry={status.reload} />
      </div>
    );
  }

  if (!assistant?.enabled) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 p-5 sm:p-8">
        <PageHead
          title={t("merchantAssistant.title")}
          sub={t("merchantAssistant.subtitle")}
        />
        <section className="rounded-3xl border bg-card p-8 shadow-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Sparkles className="h-6 w-6" />
          </div>
          <h2 className="mt-5 text-xl font-bold">
            {t("merchantAssistant.notReadyTitle")}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
            {t("merchantAssistant.notReadyBody")}
          </p>
          <p className="mt-4 inline-flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4" />
            {t("merchantAssistant.privateKeyNote")}
          </p>
        </section>
      </div>
    );
  }

  if (!assistant.consented) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 p-5 sm:p-8">
        <PageHead
          title={t("merchantAssistant.title")}
          sub={t("merchantAssistant.subtitle")}
        />
        <section className="rounded-3xl border bg-card p-6 shadow-sm sm:p-9">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-100 text-violet-700">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h2 className="mt-5 text-xl font-bold">
            {t("merchantAssistant.consentTitle")}
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
            {t("merchantAssistant.consentBody")}
          </p>
          <ul className="mt-5 list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            <li>{t("merchantAssistant.consentData1")}</li>
            <li>{t("merchantAssistant.consentData2")}</li>
            <li>{t("merchantAssistant.consentData3")}</li>
          </ul>
          <form className="mt-7 space-y-5" onSubmit={enableAssistant}>
            <label className="flex cursor-pointer items-start gap-3 rounded-xl border p-4 text-sm">
              <input
                className="mt-1"
                type="checkbox"
                checked={accepted}
                onChange={(event) => setAccepted(event.target.checked)}
              />
              <span>{t("merchantAssistant.consentCheckbox")}</span>
            </label>
            <Button type="submit" disabled={!accepted || consenting}>
              {consenting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              {t("merchantAssistant.enable")}
            </Button>
          </form>
        </section>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-[calc(100dvh-4rem)] w-full max-w-6xl flex-col gap-5 p-4 sm:p-7">
      <PageHead
        title={t("merchantAssistant.title")}
        sub={t("merchantAssistant.subtitle")}
      />

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-violet-50 px-5 py-4 text-sm text-violet-950 dark:bg-violet-950/30 dark:text-violet-100">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-violet-600" />
          <p>{t("merchantAssistant.banner")}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setConversationId(null);
            setMessages([]);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("merchantAssistant.newChat")}
        </Button>
      </div>

      <div className="grid flex-1 gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
        <section className="flex min-h-[560px] flex-col overflow-hidden rounded-3xl border bg-card shadow-sm">
          {assistant.conversations.length > 0 && (
            <div className="flex gap-2 overflow-x-auto border-b p-3">
              {assistant.conversations.slice(0, 8).map((conversation) => (
                <div
                  key={conversation.id}
                  className={cn(
                    "flex shrink-0 items-center rounded-xl border",
                    conversationId === conversation.id &&
                      "border-primary bg-primary/5",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => void openConversation(conversation.id)}
                    className="max-w-48 truncate px-3 py-2 text-xs font-medium"
                    title={conversation.title}
                  >
                    {conversation.title}
                  </button>
                  <button
                    type="button"
                    onClick={() => void deleteConversation(conversation.id)}
                    className="p-2 text-muted-foreground hover:text-destructive"
                    aria-label={t("merchantAssistant.deleteConversation")}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex-1 space-y-6 overflow-y-auto px-5 py-7 sm:px-8">
            {loadingConversation && (
              <div className="flex justify-center p-6">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            )}
            {messages.length === 0 && !loadingConversation && (
              <div className="space-y-7">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div className="pt-1">
                    <p className="font-semibold">
                      {t("merchantAssistant.greeting")}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t("merchantAssistant.greetingSub")}
                    </p>
                  </div>
                </div>
                <div>
                  <div className="mb-3 flex items-center gap-3 text-sm font-semibold text-muted-foreground">
                    <span>{t("merchantAssistant.quickActions")}</span>
                    <span className="h-px flex-1 bg-border" />
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {assistant.quick_actions.map((prompt, index) => (
                      <button
                        key={prompt}
                        type="button"
                        onClick={() =>
                          void sendMessage(
                            undefined,
                            t(`merchantAssistant.quick.${index}`),
                          )
                        }
                        disabled={busy || !socketConnected}
                        className="flex min-h-14 items-center justify-between gap-3 rounded-2xl bg-violet-50 px-4 py-3 text-left text-sm transition hover:bg-violet-100 disabled:opacity-60 dark:bg-violet-950/30 dark:hover:bg-violet-950/50"
                      >
                        <span>{t(`merchantAssistant.quick.${index}`)}</span>
                        <MessageCircle className="h-4 w-4 shrink-0 text-violet-600" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "flex gap-3",
                  message.role === "user" && "justify-end",
                )}
              >
                {message.role === "assistant" && (
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Sparkles className="h-4 w-4" />
                  </div>
                )}
                <div
                  className={cn(
                    "max-w-[88%] whitespace-pre-wrap break-words rounded-2xl px-4 py-3 text-sm leading-6",
                    message.role === "user"
                      ? "rounded-br-md bg-primary text-primary-foreground"
                      : "bg-muted/70 text-foreground",
                  )}
                >
                  {message.content}
                </div>
              </div>
            ))}
            {(busy || socketTyping) && (
              <div className="flex items-center gap-2 pl-11 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("merchantAssistant.thinking")}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form
            onSubmit={(event) => void sendMessage(event)}
            className="border-t p-3 sm:p-4"
          >
            <div className="flex items-end gap-2 rounded-2xl border bg-background p-2 focus-within:ring-2 focus-within:ring-primary/20">
              <textarea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={t("merchantAssistant.inputPlaceholder")}
                rows={2}
                maxLength={4000}
                className="max-h-36 min-h-12 flex-1 resize-y bg-transparent px-2 py-2 text-sm outline-none placeholder:text-muted-foreground"
                aria-label={t("merchantAssistant.inputPlaceholder")}
              />
              <Button
                type="submit"
                size="icon"
                disabled={busy || !socketConnected || !draft.trim()}
                aria-label={t("merchantAssistant.send")}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p
              className={cn(
                "mt-2 px-2 text-center text-[11px]",
                socketConnected ? "text-emerald-700" : "text-amber-700",
              )}
            >
              {t(
                socketConnected
                  ? "merchantAssistant.realtimeConnected"
                  : "merchantAssistant.realtimeConnecting",
              )}
            </p>
            <p className="mt-2 px-2 text-center text-[11px] text-muted-foreground">
              {t("merchantAssistant.disclaimer")}
            </p>
          </form>
        </section>

        <aside className="space-y-4">
          <section className="rounded-2xl border bg-card p-4">
            <div className="mb-3 flex items-start gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
                <Clock3 className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-sm font-bold">
                  {t("merchantAssistant.scheduledWork")}
                </h2>
                <p className="text-xs leading-5 text-muted-foreground">
                  {t("merchantAssistant.scheduledWorkSub")}
                </p>
                <p className="mt-2 text-[11px] leading-5 text-muted-foreground">
                  {t("merchantAssistant.scheduledWorkPrivacy")}
                </p>
              </div>
            </div>
            <form
              onSubmit={(event) => void createJob(event)}
              className="space-y-2 border-b pb-4"
            >
              <Label htmlFor="assistant-job-template" className="sr-only">
                {t("merchantAssistant.jobTemplate")}
              </Label>
              <select
                id="assistant-job-template"
                className="h-9 w-full rounded-md border bg-background px-2 text-xs"
                value={jobTemplate}
                onChange={(event) =>
                  setJobTemplate(event.target.value as AssistantJob["template"])
                }
              >
                {(
                  [
                    "DAILY_PAYMENT_DIGEST",
                    "WEEKLY_ACCOUNT_HEALTH",
                    "PENDING_PAYMENT_REVIEW",
                  ] as const
                ).map((template) => (
                  <option key={template} value={template}>
                    {t(`merchantAssistant.templates.${template}`)}
                  </option>
                ))}
              </select>
              <div className="flex gap-2">
                <select
                  className="h-9 min-w-0 flex-1 rounded-md border bg-background px-2 text-xs"
                  value={jobCadence}
                  onChange={(event) =>
                    setJobCadence(event.target.value as AssistantJob["cadence"])
                  }
                >
                  <option value="DAILY">{t("merchantAssistant.daily")}</option>
                  <option value="WEEKLY">
                    {t("merchantAssistant.weekly")}
                  </option>
                </select>
                <Input
                  className="h-9 w-24 text-xs"
                  type="time"
                  value={jobTime}
                  onChange={(event) => setJobTime(event.target.value)}
                />
              </div>
              <Button
                className="w-full"
                size="sm"
                type="submit"
                disabled={savingJob || assistant.jobs.length >= 20}
              >
                {savingJob ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                {t("merchantAssistant.scheduleReport")}
              </Button>
            </form>
            <div className="mt-3 space-y-2">
              {assistant.jobs.map((job) => (
                <div key={job.id} className="rounded-xl border p-3">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold">{job.name}</p>
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {job.cadence === "DAILY"
                          ? t("merchantAssistant.daily")
                          : t("merchantAssistant.weekly")}{" "}
                        · {job.local_time} · {job.timezone}
                      </p>
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        {job.enabled
                          ? t("merchantAssistant.nextRun")
                          : t("merchantAssistant.paused")}
                        : {new Date(job.next_run_at).toLocaleString()}
                      </p>
                    </div>
                    <button
                      type="button"
                      className={cn(
                        "rounded-full px-2 py-1 text-[10px]",
                        job.enabled
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-muted text-muted-foreground",
                      )}
                      onClick={() => void toggleJob(job)}
                    >
                      {job.enabled
                        ? t("merchantAssistant.enabled")
                        : t("merchantAssistant.paused")}
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[11px]"
                      disabled={!job.enabled}
                      onClick={() => void runJob(job)}
                    >
                      {t("merchantAssistant.runNow")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-[11px]"
                      onClick={() => {
                        const next = expandedJob === job.id ? null : job.id;
                        setExpandedJob(next);
                        if (next) void loadJobRuns(job.id);
                      }}
                    >
                      {t("merchantAssistant.viewReports")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-[11px] text-destructive"
                      onClick={() => void deleteJob(job)}
                    >
                      {t("merchantAssistant.deleteJob")}
                    </Button>
                  </div>
                  {expandedJob === job.id && (
                    <div className="mt-3 space-y-2 border-t pt-3">
                      {(jobRuns[job.id] ?? []).map((run) => (
                        <div
                          key={run.id}
                          className="rounded-lg bg-muted/60 p-2"
                        >
                          <p className="text-[10px] font-semibold">
                            {t(`merchantAssistant.runStatus.${run.status}`)} ·{" "}
                            {new Date(run.created_at).toLocaleString()}
                          </p>
                          {run.report && (
                            <p className="mt-1 whitespace-pre-wrap text-xs leading-5">
                              {run.report}
                            </p>
                          )}
                        </div>
                      ))}
                      {(jobRuns[job.id] ?? []).length === 0 && (
                        <p className="text-xs text-muted-foreground">
                          {t("merchantAssistant.noReports")}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {assistant.jobs.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {t("merchantAssistant.noScheduledWork")}
                </p>
              )}
            </div>
          </section>
          <section className="rounded-2xl border bg-card p-4">
            <button
              type="button"
              onClick={() => setShowMemories((show) => !show)}
              className="flex w-full items-center gap-3 text-left"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                <Brain className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-bold">
                  {t("merchantAssistant.memories")}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {t("merchantAssistant.memoriesCount", {
                    count: memories.length,
                  })}
                </p>
              </div>
              <span className="text-xs text-primary">
                {showMemories
                  ? t("merchantAssistant.hide")
                  : t("merchantAssistant.manage")}
              </span>
            </button>
            {showMemories && (
              <div className="mt-4 space-y-4 border-t pt-4">
                <p className="text-xs leading-5 text-muted-foreground">
                  {t("merchantAssistant.memoryDescription")}
                </p>
                <form onSubmit={saveMemory} className="space-y-2">
                  <Label htmlFor="assistant-memory" className="sr-only">
                    {t("merchantAssistant.memoryLabel")}
                  </Label>
                  <Input
                    id="assistant-memory"
                    value={memoryDraft}
                    onChange={(event) => setMemoryDraft(event.target.value)}
                    maxLength={1000}
                    placeholder={t("merchantAssistant.memoryPlaceholder")}
                  />
                  <Button
                    className="w-full"
                    size="sm"
                    type="submit"
                    disabled={savingMemory || !memoryDraft.trim()}
                  >
                    {savingMemory ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="mr-2 h-4 w-4" />
                    )}
                    {t("merchantAssistant.saveMemory")}
                  </Button>
                </form>
                <div className="space-y-2">
                  {memories.map((memory) => (
                    <div
                      key={memory.id}
                      className="flex items-start gap-2 rounded-xl bg-muted/60 p-3"
                    >
                      <p className="flex-1 whitespace-pre-wrap break-words text-xs leading-5">
                        {memory.content}
                      </p>
                      <button
                        type="button"
                        disabled={deletingMemory === memory.id}
                        onClick={() => void deleteMemory(memory.id)}
                        className="text-muted-foreground hover:text-destructive"
                        aria-label={t("merchantAssistant.deleteMemory")}
                      >
                        {deletingMemory === memory.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  ))}
                  {memories.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      {t("merchantAssistant.noMemories")}
                    </p>
                  )}
                </div>
              </div>
            )}
          </section>
          <div className="flex items-start gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-xs leading-5 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            {t("merchantAssistant.privacyNote")}
          </div>
          <div className="flex items-start gap-2 px-1 text-xs leading-5 text-muted-foreground">
            <Clock3 className="mt-0.5 h-4 w-4 shrink-0" />
            {t("merchantAssistant.contextFreshness")}
          </div>
        </aside>
      </div>
    </div>
  );
}
